import React, { useState } from 'react';
import { IconClose } from './Icons';
import './VerifyEmailBanner.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

/**
 * A quiet in-app nudge to confirm the account's email address.
 *
 * Verification is no longer a front-door gate (gtm-playbook.md §8c #1) — the
 * account is fully usable unverified. What it still gates is *outbound*:
 * inviting people and minting public share links. So this is a dismissible
 * strip, not a wall, and it says what is actually held rather than "please
 * verify" with no stated consequence. It renders only when the server reports
 * `emailVerified: false`, which can only happen on a deployment that can
 * actually send the link.
 */
export default function VerifyEmailBanner({ user, onDismiss }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.emailVerified !== false) return null;

  const handleResend = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${SERVER_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      if (!res.ok) throw new Error('resend failed');
      setSent(true);
    } catch {
      setError('Could not send — try again in a moment.');
    }
    setBusy(false);
  };

  return (
    <div className="verify-banner" role="status">
      <div className="verify-banner-text">
        <strong>Confirm your email</strong>
        <span>
          {sent
            ? `A fresh link is on its way to ${user.email}.`
            : `Click the link we sent to ${user.email} to unlock inviting teammates and sharing decisions publicly. Everything else already works.`}
        </span>
        {error && <span className="verify-banner-error">{error}</span>}
      </div>
      <div className="verify-banner-actions">
        {!sent && (
          <button
            type="button"
            className="verify-banner-btn"
            disabled={busy}
            onClick={handleResend}
          >
            {busy ? 'Sending…' : 'Resend link'}
          </button>
        )}
        <button
          type="button"
          className="verify-banner-close"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <IconClose size={16} />
        </button>
      </div>
    </div>
  );
}
